// Channel + people matching for the top search bar.
// Pure: callers pass the workspace data in, so a mobile search surface can reuse it.
import type { Channel, Message, PageSearchResult, TaskSearchResult } from "$lib/core/types.js";
import { cyboNameOf } from "$lib/agent-display.js";
import { reportUiFailure, shouldReport } from "$lib/report-failure.js";

export type BrowseChannel = Channel & { isMember: boolean; memberCount: number };

export interface ChannelHit {
  channel: Channel;
  kind: "channel" | "groupdm";
  isMember: boolean;
  memberCount: number | null;
  score: number;
}

export interface PersonCandidate {
  id: string;
  kind: "dm" | "agent";
  name: string;
  email: string | null;
  // A second name the query may match: a renamed session's default name ("Claude").
  alsoMatches?: string;
  // No longer in the workspace (relay-flagged): offered greyed, labelled.
  gone?: "deactivated" | "deleted";
}

export interface PersonHit extends PersonCandidate {
  score: number;
}

// One row per agent identity (the roster holds one entry per session). A cybo is addressed by
// cybo id (session-scope dmAddress: the route resolves it to the current session); one the
// roster lost is addressed by a live session's agentId so the row still opens. Aliases set on a
// cybo's sessions stay searchable. Closed sessions never add a row.
export interface AgentTarget {
  id: string;
  name: string;
  alsoMatches?: string;
}

export function agentIdentityTargets<
  A extends {
    agentId: string;
    cyboId?: string | null;
    cyboName?: string | null;
    lifecycle: string;
  },
>(
  agents: readonly A[],
  cybos: ReadonlyArray<{ id: string; name: string }>,
  sessionName: (a: A) => string,
  defaultName: (a: A) => string,
): AgentTarget[] {
  const open = agents.filter((a) => a.lifecycle !== "closed");
  const byCybo = new Map<string, A[]>();
  for (const a of open) if (a.cyboId) byCybo.set(a.cyboId, [...(byCybo.get(a.cyboId) ?? []), a]);
  const aliasTerms = (name: string, rows: A[] = []) =>
    [...new Set(rows.map(sessionName).filter((n) => n !== name))].join(" ") || undefined;
  const out: AgentTarget[] = [];
  const seen = new Set<string>();
  for (const c of cybos) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    out.push({ id: c.id, name: c.name, alsoMatches: aliasTerms(c.name, byCybo.get(c.id)) });
  }
  for (const a of open) {
    const key = a.cyboId ?? a.agentId;
    if (seen.has(key)) continue;
    seen.add(key);
    if (!a.cyboId) {
      out.push({ id: key, name: sessionName(a), alsoMatches: defaultName(a) });
      continue;
    }
    const name = cyboNameOf(a) ?? sessionName(a);
    out.push({ id: a.agentId, name, alsoMatches: aliasTerms(name, byCybo.get(a.cyboId)) });
  }
  return out;
}

export const MIN_QUERY_LENGTH = 2;

// Rows per result section before "Show N more": five keeps every section on screen.
export const SEARCH_SECTION_PREVIEW = 5;

// -1 = no match. A prefix beats a word start, which beats any other substring.
export function matchScore(text: string | null | undefined, query: string): number {
  if (!text || !query) return -1;
  const hay = text.toLowerCase();
  const idx = hay.indexOf(query.toLowerCase());
  if (idx === -1) return -1;
  if (idx === 0) return 3;
  return /[\s\-_/.@]/.test(hay[idx - 1] ?? "") ? 2 : 1;
}

// Joined channels (the sidebar's list) merged with the browse list, which adds the
// public channels the viewer has not joined. A private channel the viewer is not in
// is dropped here too, on top of the server never sending one.
export function matchChannels(
  joined: readonly Channel[],
  browse: readonly BrowseChannel[] | null,
  query: string,
  limit = 8,
): ChannelHit[] {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return [];
  const byId = new Map<string, ChannelHit>();
  for (const ch of joined) {
    if (ch.isArchived) continue;
    const score = matchScore(ch.name, q);
    if (score < 0) continue;
    const kind = ch.type === "group_dm" || ch.isHidden === true ? "groupdm" : "channel";
    byId.set(ch.id, { channel: ch, kind, isMember: true, memberCount: null, score });
  }
  for (const row of browse ?? []) {
    if (row.isArchived || row.isHidden || row.type === "group_dm") continue;
    if (row.isPrivate && !row.isMember) continue;
    const score = matchScore(row.name, q);
    if (score < 0) continue;
    const known = byId.get(row.id);
    if (known) {
      known.memberCount = row.memberCount;
      continue;
    }
    byId.set(row.id, {
      channel: row,
      kind: "channel",
      isMember: row.isMember,
      memberCount: row.memberCount,
      score,
    });
  }
  return [...byId.values()]
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.isMember) - Number(a.isMember) ||
        a.channel.name.localeCompare(b.channel.name),
    )
    .slice(0, limit);
}

export function matchPeople(
  candidates: readonly PersonCandidate[],
  query: string,
  limit = 6,
): PersonHit[] {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return [];
  const hits: PersonHit[] = [];
  for (const c of candidates) {
    // Name + handle/alias only — never email (an address fragment isn't a name the user means).
    const score = Math.max(matchScore(c.name, q), matchScore(c.alsoMatches, q));
    if (score >= 0) hits.push({ ...c, score });
  }
  return hits
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(a.kind === "agent") - Number(b.kind === "agent") ||
        a.name.localeCompare(b.name),
    )
    .slice(0, limit);
}

// [before, match, after] around the first case-insensitive hit, rendered as text.
export function highlightParts(text: string, query: string): [string, string, string] {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return [text, "", ""];
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return [text, "", ""];
  return [text.slice(0, idx), text.slice(idx, idx + q.length), text.slice(idx + q.length)];
}

// A broken leg fails every debounced query; the server already reported the same failure,
// so the client reports it once per leg per window (the composer page picker's window).
export const SEARCH_FAILURE_REPORT_MS = 300_000;

export function reportSearchLegFailure(
  name: "messages" | "tasks" | "pages" | "skills",
  reason: unknown,
  workspaceId: string,
): void {
  if (shouldReport(`search.${name}:${workspaceId}`, SEARCH_FAILURE_REPORT_MS)) {
    reportUiFailure(`search.${name}`, reason);
  }
}

// A search leg that rejected is a failure of its own section, never an empty one: the
// tasks and pages legs of the top search bar (desktop dropdown and mobile overlay) settle
// through this so a broken leg reads "Couldn't search …", not "no results".
export function settledLeg<T>(
  leg: PromiseSettledResult<T[]>,
  name: "tasks" | "pages" | "skills",
  workspaceId: string,
): { value: T[]; failed: boolean } {
  if (leg.status === "fulfilled") return { value: leg.value, failed: false };
  reportSearchLegFailure(name, leg.reason, workspaceId);
  return { value: [], failed: true };
}

// The sections ordered by their freshest hit. Skills is not one of them: it always comes last.
export type RankedSection = "messages" | "tasks" | "pages";
export type SearchSection = RankedSection | "skills";

// Tie and no-timestamp fallback: the order the sections shipped in.
const DEFAULT_SECTION_ORDER: readonly RankedSection[] = ["messages", "tasks", "pages"];

// The newest timestamp among a section's hits; null when it has none (or none carries one).
export function freshestOf(times: Iterable<number | null | undefined>): number | null {
  let best: number | null = null;
  for (const t of times) if (typeof t === "number" && (best === null || t > best)) best = t;
  return best;
}

// Result sections by their freshest hit, newest first, so a page written a minute ago is not
// buried under twenty old messages. A section with no timestamp (empty, or its search failed:
// pass null) goes after every one that has one; ties keep the default order.
export function orderSections(freshest: Record<RankedSection, number | null>): RankedSection[] {
  return DEFAULT_SECTION_ORDER.map((section, i) => ({ section, i, t: freshest[section] }))
    .sort((a, b) => {
      if (a.t === b.t) return a.i - b.i;
      if (a.t === null) return 1;
      if (b.t === null) return -1;
      return b.t - a.t;
    })
    .map((x) => x.section);
}

// The freshest hit per section, over the rows its preview shows. A section passed as null (its
// search failed, or is still loading) has no timestamp, so orderSections puts it last.
export function sectionFreshness(sections: {
  messages: readonly Message[] | null;
  tasks: readonly TaskSearchResult[] | null;
  pages: readonly PageSearchResult[] | null;
}): Record<RankedSection, number | null> {
  const freshest = <T>(rows: readonly T[] | null, at: (row: T) => number | null | undefined) =>
    rows ? freshestOf(rows.slice(0, SEARCH_SECTION_PREVIEW).map(at)) : null; // null: a section with no timestamp sorts last (orderSections)
  return {
    messages: freshest(sections.messages, (m) => m.createdAt),
    tasks: freshest(sections.tasks, (t) => t.updatedAt),
    pages: freshest(sections.pages, (p) => p.updatedAt),
  };
}
